import React, { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import Avatar from '@rover/components/src/Avatar';
import { Box, Button, Flex, Paragraph, Text } from '@rover/kibble/core';
import { Form, useKibbleForm } from '@rover/kibble/forms';
import TextField from '@rover/kibble/forms/fields/TextField';
import Dropzone from '@rover/react-lib/src/components/utils/Dropzone';
import RoverLoading from '@rover/react-lib/src/components/utils/RoverLoading';
import { useApiPersonDetailImagesCreate } from '@rover/rsdk/src/apiClient/latest/api-person-detail-images-create';
import { useApiPersonDetailImagesProfilePhotoUpdate } from '@rover/rsdk/src/apiClient/latest/api-person-detail-images-profile-photo-update';
import { getApiProviderProfileModuleRetrieveQueryKey } from '@rover/rsdk/src/apiClient/latest/api-provider-profile-module-retrieve';
import type { ExperienceModuleProfilePartial } from '@rover/rsdk/src/apiClient/latest/model/experienceModuleProfilePartial';
import type { Image } from '@rover/rsdk/src/apiClient/latest/model/image';
import type { PhotosModule } from '@rover/rsdk/src/apiClient/latest/model/photosModule';
import {
  MediaFile,
  MediaType,
  useSelectMedia,
  useUploadMedia,
} from '@rover/rsdk/src/modules/Media';
import { getWordCount } from '@rover/utilities/wordCount';

import { MODULE_KEYS } from '../../constants/moduleKeys';
import { type DogCareDetailsFormData, dogCareDetailsSchema, MIN_WORD_COUNT } from '../schema';

type DogCareDetailsFormProps = {
  experienceModule: ExperienceModuleProfilePartial | undefined;
  photosModule: PhotosModule | undefined;
  onSubmit: (data: DogCareDetailsFormData) => void;
  isSubmitting: boolean;
  userOpk: string;
  photosData: any;
  queryClient: any;
  setFormIsDirty: (isDirty: boolean) => void;
};

const DogCareDetailsForm: React.FC<DogCareDetailsFormProps> = ({
  experienceModule,
  photosModule,
  onSubmit,
  isSubmitting,
  userOpk,
  photosData,
  queryClient,
  setFormIsDirty,
}) => {
  const { t } = useLingui();
  const [uploadError, setUploadError] = useState<string>('');

  const { mutate: mutateCreateImage, isLoading: isCreatingImage } =
    useApiPersonDetailImagesCreate();
  const { mutate: mutateSetProfilePhoto, isLoading: isSettingProfilePhoto } =
    useApiPersonDetailImagesProfilePhotoUpdate();

  const { isUploading, uploadMedia } = useUploadMedia();

  const isPhotoLoading = isUploading || isCreatingImage || isSettingProfilePhoto;

  // form default values - now guaranteed to have data because component only renders when data exists
  const defaultValues = {
    headline: experienceModule?.headline ?? '',
    experience: experienceModule?.experience ?? '',
    yearsOfExperience: experienceModule?.yearsOfExperience ?? 0,
    imageUrl: photosModule?.profilePhoto?.bigThumb ?? '',
  };

  const {
    configureField,
    formProps,
    submitButtonProps,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useKibbleForm({
    schema: dogCareDetailsSchema,
    defaultValues,
    mode: 'onChange',
    onValidSubmit: onSubmit,
    onInvalidSubmit: () => {},
  });

  const { dropzoneProps } = useSelectMedia({
    multiple: false,
    type: MediaType.IMAGE,
  });

  const onImageSelect = (file: File): void => {
    const mediaFile = new MediaFile(file);

    // clear upload errors
    setUploadError('');

    // local preview of uploaded image
    setValue('imageUrl', mediaFile.uri);

    // upload to media service
    uploadMedia([{ file: mediaFile, opts: { objectType: 'people' } }], {
      onUploadSuccess({ result }) {
        const uploadedUrl = result.value.url;

        // update form state with the uploaded image
        setValue('imageUrl', uploadedUrl);

        // create Image record from S3 upload
        mutateCreateImage(
          {
            opk: userOpk,
            data: { imageUrl: uploadedUrl } as Image,
          },
          {
            onSuccess: (createdImage) => {
              // set the created image as profile photo
              mutateSetProfilePhoto(
                {
                  opk: userOpk,
                  data: { url: createdImage.url } as Image,
                },
                {
                  onSuccess: () => {
                    const updatedPhotosModule = {
                      ...photosData,
                      data: {
                        ...photosModule,
                        profilePhoto: createdImage,
                      },
                    };
                    queryClient.setQueryData(
                      getApiProviderProfileModuleRetrieveQueryKey(MODULE_KEYS.PHOTOS_V2),
                      updatedPhotosModule
                    );
                  },
                }
              );
            },
            onError: () => {
              setUploadError('Failed to save image. Please try again.');
            },
          }
        );
      },
      onUploadFailed() {
        setUploadError('Failed to upload image. Please try again.');
      },
    });
  };

  useEffect(() => {
    setFormIsDirty(isDirty);
  }, [isDirty, setFormIsDirty]);

  return (
    <Form {...formProps}>
      <Flex alignItems="flex-start" marginBottom="10x" gap="4x" flexDirection="column">
        <Flex alignItems="flex-start" gap="4x" width="100%">
          <Dropzone
            {...dropzoneProps}
            onDropAccepted={(files) => {
              if (files[0]) {
                onImageSelect(files[0]);
              }
            }}
          >
            <Box sx={{ cursor: 'pointer' }}>
              <RoverLoading isLoading={isPhotoLoading}>
                <Avatar
                  imageUrl={watch('imageUrl') ?? null}
                  diameter={64}
                  borderRadius="50%"
                  objectFit="cover"
                />
              </RoverLoading>
            </Box>
          </Dropzone>

          <Flex flexDirection="column" flex="1" alignSelf="center">
            <Text size="200" fontWeight="semibold">
              {t`Photo`}
            </Text>
            <Paragraph size="100" textColor="tertiary" m="0x">
              {t`Use a well-lit, clear photo of your face without sunglasses.`}
            </Paragraph>
          </Flex>
        </Flex>
        {errors.imageUrl && (
          <Text size="100" textColor="error">
            {errors.imageUrl.message as string}
          </Text>
        )}
        {uploadError && (
          <Text size="100" textColor="error">
            {uploadError}
          </Text>
        )}
      </Flex>

      <TextField
        {...configureField('headline')}
        primaryLabel={t`Headline`}
        secondaryLabel={t`Make your headline short, descriptive and genuine.`}
        variantOptions={{
          currentCharacterCount: watch('headline').length,
        }}
        required={false}
      />

      <TextField
        {...configureField('experience')}
        primaryLabel={t`Your experience`}
        secondaryLabel={t`What sets you apart from other sitters? Be sure to include any special skills like training young pets or senior care.`}
        placeholder={t`I've taken care of dogs of every kind since I was 10. Neighbors, friends, and family have relied on me to check in with, feed and play with their pets...`}
        variant="textarea"
        variantOptions={{
          minHeight: ['108px', '144px'],
          minimumWordCount: MIN_WORD_COUNT,
          currentWordCount: getWordCount(watch('experience') || '') || 0,
        }}
        required={false}
      />

      <TextField
        {...configureField('yearsOfExperience')}
        primaryLabel={t`Years of experience`}
        inputType="number"
        fieldWidth="100px"
        required
        requirednessLabel="hide"
      />

      <Flex flexDirection="row" alignItems="center" justifyContent="center" pt="6x" width="100%">
        <Box flexGrow="1">
          <Button
            variant="primary"
            fullWidth
            {...submitButtonProps}
            loading={isSubmitting}
            disabled={submitButtonProps?.disabled || isPhotoLoading}
          >
            {t`Save & Continue`}
          </Button>
        </Box>
      </Flex>
    </Form>
  );
};

export default DogCareDetailsForm;
